import "../saml-configuration.module.css";

import { useState, useReducer, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { Row, Col } from "react-bootstrap";
import { cloneDeep } from "lodash";

// Hook
import useMessage from "hooks/useMessage";

// Const
import { metadataSetField } from "../const";

// Slice
import { uiActions, selectShowModalSpinner } from "stores/slice/ui";

// UI
import SpinnerForModal from "ui/SpinnerForModal";

// Component
import Button from "components/Button";
import Input from "components/Input";
import RadioButton from "components/RadioButton";
import Textarea from "components/Textarea";
import ModalContainer from "components/ModalContainer";
import MessageBoxGroup from "components/MessageBoxGroup";
import InputWithUploadButton from "components/InputWithUploadButton";

// Model
import MetadataSetModel from "../models/MetadataSet";

// Control
import controller from "../controller";

// Store
import metadataSetStore from "../stores/metadataSet";

const AddAndEditTrustedIdentityProviderModal = props => {
  const {
    selectedOrg,
    modalStatus,
    changeModalStatus,
    selectedMetadataSet = null,
    successCallback
  } = props;

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const showModalSpinner = useSelector(selectShowModalSpinner);

  // State
  const [metadataSetState, accountDispatch] = useReducer(
    metadataSetStore.reducer,
    metadataSetStore.defaultState
  );

  // Hook
  const {
    messages,
    messagesType,
    resetMessages,
    setErrorMessage
  } = useMessage();

  // Method
  const init = useCallback(() => {
    accountDispatch({
      type: metadataSetStore.action.initializeState,
      payload: MetadataSetModel.getDefaultMetadataSetState(selectedMetadataSet)
    });
  }, [selectedMetadataSet]);

  const changeField = (field, value) => {
    const getMethodName = (str) => {
      const firstLetter = str.charAt(0);
      const firstLetterCap = firstLetter.toUpperCase();
      const remainingLetters = str.slice(1);

      return firstLetterCap + remainingLetters;
    }

    const updatedField = cloneDeep(metadataSetState[field]);
    const methodName = `change${getMethodName(field)}`;
    MetadataSetModel[methodName](updatedField, value, metadataSetState);

    accountDispatch({
      type: metadataSetStore.action[methodName],
      payload: updatedField
    });
  }

  const checkForm = () => {
    let state = true;

    for (const fieldKey in metadataSetState) {
      if (
        fieldKey !== metadataSetField.byManualOrImport &&
        metadataSetState[fieldKey].byManualOrImport === metadataSetState.byManualOrImport.value
      ) {
        const { valid } = metadataSetState[fieldKey];

        if (!valid) {
          state = false;
        }
      }
    }

    return state;
  }

  const addOrSave = async () => {
    try {
      const selectedMetadataSetId = selectedMetadataSet?.rawData.id || null;
      const response = await controller.addOrSaveMetadataSet(selectedOrg.id, selectedMetadataSetId, metadataSetState);

      if (response.error) {
        throw response.error;
      }

      dispatch(uiActions.hideModalSpinner());
      resetMessages();
      changeModalStatus('addAndEdit', false);
      successCallback();
    } catch (error) {
      setErrorMessage(error.code);
      dispatch(uiActions.hideModalSpinner());
    }
  }

  // Side effect
  useEffect(() => {
    if (modalStatus.addAndEdit.status) {
      dispatch(uiActions.showModalSpinner());
      init();
      setTimeout(() => {
        dispatch(uiActions.hideModalSpinner());
      }, 1000);
    }
  }, [dispatch, init, modalStatus.addAndEdit.status]);

  return (
    <ModalContainer
      modalWidthType="modal-500px"
      openModal={modalStatus.addAndEdit.status}
      closeModal={() => changeModalStatus('addAndEdit', false)}
    >
      {showModalSpinner && <SpinnerForModal />}
      <div className="header">
        <div className="title">
          {!selectedMetadataSet && t('b0b9b339d4')}
          {selectedMetadataSet && t('89937cc2b6')}
        </div>
      </div>
      <div className="body">
        <MessageBoxGroup
          messages={messages}
          ErrorMessagesType={messagesType.error.server}
          onClose={resetMessages}
        />

        {/* Name */}
        <div className="mb-3">
          <div className="modal-form-title required">{t('49ee308734')}</div>
          <Input
            type="text"
            placeholder="7563cde047"
            isInvalid={metadataSetState.name.dirty && !metadataSetState.name.valid}
            value={metadataSetState.name.value}
            onChange={e => changeField(metadataSetField.name, e.target.value)}
          />
        </div>
        {/* Manual configuration */}
        <div>
          <div className="modal-form-title">
            <RadioButton
              id="manualConfiguration"
              name="manualConfiguration"
              label="8fda09df80"
              isInvalid={metadataSetState.name.dirty && !metadataSetState.name.valid}
              checked={metadataSetState.byManualOrImport.value}
              labelClassName="form-title"
              onChange={e => changeField(metadataSetField.byManualOrImport, true)}
            />
          </div>
        </div>
        {/* Manual configuration content */}
        <div className={`ms-4 ${metadataSetState.byManualOrImport.value ? 'd-block' : 'd-none'}`}>
          <Row>
            <Col sm={12}>
              <div className="modal-form-title">{t('ad882e4bf6')}</div>
              <div>
                <Input
                  type="text"
                  style={{ paddingLeft: 15 }}
                  placeholder="d47a044d18"
                  isInvalid={metadataSetState.url.dirty && !metadataSetState.url.valid}
                  value={metadataSetState.url.value}
                  onChange={e => changeField(metadataSetField.url, e.target.value)}
                />
              </div>
            </Col>
            <Col sm={12}>
              <div className="modal-form-title">{t('62ed2c4660')}</div>
              <div>
                <Input
                  type="text"
                  style={{ paddingLeft: 15 }}
                  value={metadataSetState.issuer.value}
                  placeholder="67d9312521"
                  isInvalid={metadataSetState.issuer.dirty && !metadataSetState.issuer.valid}
                  onChange={e => changeField(metadataSetField.issuer, e.target.value)}
                />
              </div>
            </Col>
            <Col sm={12}>
              <div className="modal-form-title required">{t('0175d1985e')}</div>
              <div>
                <InputWithUploadButton
                  id="certificate"
                  style={{ paddingLeft: 15 }}
                  accept='.pem'
                  placeholder='828691b5aa'
                  isInvalid={metadataSetState.certificate.dirty && !metadataSetState.certificate.valid}
                  value={metadataSetState.certificate.value ? metadataSetState.certificate.value : ''}
                  onClick={e => !selectedMetadataSet && changeField(metadataSetField.certificate, null)}
                  onChange={e => { changeField(metadataSetField.certificate, e.target.files[0]) }}
                />
              </div>
            </Col>
            <Col sm={12}>
              <div className="modal-form-title">{t('dd3f69657c')}</div>
              <div>
                <Input
                  type="text"
                  style={{ paddingLeft: 15 }}
                  isInvalid={metadataSetState.logoutUrl.dirty && !metadataSetState.logoutUrl.valid}
                  value={metadataSetState.logoutUrl.value}
                  placeholder="06c9cdba97"
                  onChange={e => changeField(metadataSetField.logoutUrl, e.target.value)}
                />
              </div>
            </Col>
            <Col sm={12}>
              <div className="modal-form-title">
                {t('34e6045742')}
              </div>
              <div>
                <Textarea
                  style={{ height: 60, paddingLeft: 23 }}
                  value={metadataSetState.fingerprint.value}
                  onChange={e => changeField(metadataSetField.fingerprint, e.target.value)}
                />
              </div>
            </Col>
          </Row>
        </div>

        {/* Import a IdP metadata */}
        <div className="mt-3">
          <div className="modal-form-title">
            <RadioButton
              id="importIdPMetadata"
              name="importIdPMetadata"
              label="07b870ebd6"
              checked={!metadataSetState.byManualOrImport.value}
              labelClassName="form-title"
              onChange={e => changeField(metadataSetField.byManualOrImport, false)}
            />
          </div>
        </div>
        {/* Import a IdP metadata content */}
        <div className={`mb-3 ms-4 ${!metadataSetState.byManualOrImport.value ? 'd-block' : 'd-none'}`}>
          <Row>
            <Col sm={12}>
              <div>
                <InputWithUploadButton
                  id="metadataSetXml"
                  accept=".xml"
                  isInvalid={metadataSetState.metadataSetXml.dirty && !metadataSetState.metadataSetXml.valid}
                  value={metadataSetState.metadataSetXml.value ? metadataSetState.metadataSetXml.value : ''}
                  onClick={e => !selectedMetadataSet && changeField(metadataSetField.metadataSetXml, null)}
                  onChange={e => { changeField(metadataSetField.metadataSetXml, e.target.files[0]) }}
                />
              </div>
            </Col>
          </Row>
        </div>
      </div>
      <div className="footer">
        <Button
          label="4a53b84974"
          className="btn-cancel"
          onClick={() => changeModalStatus('addAndEdit', false)}
        />
        <Button
          label="c9cc8cce24"
          className="btn-submit"
          disabled={!checkForm()}
          onClick={() => addOrSave()}
        />
      </div>
    </ModalContainer>
  );
};

export default AddAndEditTrustedIdentityProviderModal;
